import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ago, formatBytes, useApi, usePoll, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Inspector, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';

export interface Screen {
  id: string; name: string; locationId: string; locationName: string; timezone: string; orientation: string; width: number; height: number; tags: string[];
  deviceId: string | null; deviceStatus: string | null; platform: string | null; appVersion: string | null; connectivity: string; heartbeatAgeSeconds: number | null;
  desiredSequence: number; activeSequence: number | null; stagedSequence: number | null; playerState: string | null; lastConfirmedPlayAt: string | null;
  capabilities: Record<string, unknown> | null; keyStorage: string | null; lastHeartbeat: Record<string, unknown> | null; acousticZone: string | null; isAudioMaster: boolean;
}

const SAVED_FILTERS = 'lumacast.screenFilters';

export function Screens() {
  const org = useOrg();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState<'grid' | 'list'>(() => (localStorage.getItem('lumacast.screenView') as 'grid' | 'list') ?? 'grid');
  const [filter, setFilter] = useState<{ search: string; connectivity: string; locationId: string }>(() => JSON.parse(localStorage.getItem(SAVED_FILTERS) ?? 'null') ?? { search: '', connectivity: '', locationId: '' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTag, setBulkTag] = useState('');
  const toast = useToast();
  const { data, error, loading, reload } = useApi<{ screens: Screen[] }>(orgPath(org, '/screens'), [org.id]);
  const locations = useApi<{ locations: { id: string; name: string }[] }>(orgPath(org, '/locations'), [org.id]);
  usePoll(useCallback(() => void reload(), [reload]), 10_000);
  useEffect(() => localStorage.setItem(SAVED_FILTERS, JSON.stringify(filter)), [filter]);
  useEffect(() => localStorage.setItem('lumacast.screenView', view), [view]);
  const screens = useMemo(
    () => (data?.screens ?? []).filter((s) =>
      (!filter.search || `${s.name} ${s.locationName} ${s.tags.join(' ')}`.toLowerCase().includes(filter.search.toLowerCase())) &&
      (!filter.connectivity || s.connectivity === filter.connectivity) && (!filter.locationId || s.locationId === filter.locationId)),
    [data, filter],
  );
  const open = params.get('screen');
  const pairing = params.get('pair') === '1';

  async function bulkApply() {
    try {
      await api('POST', orgPath(org, '/screens/bulk'), { screenIds: [...selected], addTags: bulkTag ? [bulkTag] : [] });
      toast(`Tagged ${selected.size} screen(s) “${bulkTag}”`);
      setSelected(new Set());
      setBulkTag('');
      void reload();
    } catch (e) {
      toast((e as Error).message, 'danger');
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Screens</h1>
          <p>Physical screens and the players installed on them. Replacing a player keeps the screen’s history.</p>
        </div>
        {can(org, 'screens.pair') && (
          <button className="btn btn-primary" onClick={() => setParams({ pair: '1' })}>
            <Icon name="plus" /> Pair screen
          </button>
        )}
      </div>
      <div className="row" style={{ marginBottom: 16 }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search screens, locations, tags" aria-label="Search screens" value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} />
        <select className="input" style={{ width: 'auto' }} aria-label="Connection filter" value={filter.connectivity} onChange={(e) => setFilter({ ...filter, connectivity: e.target.value })}>
          <option value="">All connections</option>
          <option value="connected">Connected</option>
          <option value="stale">Stale</option>
          <option value="offline">Offline</option>
          <option value="unknown">Not paired</option>
        </select>
        <select className="input" style={{ width: 'auto' }} aria-label="Location filter" value={filter.locationId} onChange={(e) => setFilter({ ...filter, locationId: e.target.value })}>
          <option value="">All locations</option>
          {locations.data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <div className="segmented" role="group" aria-label="View">
          <button aria-pressed={view === 'grid'} onClick={() => setView('grid')}><Icon name="grid" size={14} /> Grid</button>
          <button aria-pressed={view === 'list'} onClick={() => setView('list')}><Icon name="list" size={14} /> List</button>
        </div>
        {selected.size > 0 && can(org, 'screens.manage') && (
          <div className="row" role="region" aria-label="Bulk actions">
            <span className="subtle">{selected.size} selected</span>
            <input className="input" style={{ width: 140 }} placeholder="Tag" aria-label="Tag to add" value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} />
            <button className="btn btn-sm" disabled={!bulkTag} onClick={() => void bulkApply()}>Add tag</button>
          </div>
        )}
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && screens.length === 0 && (
        <div className="card">
          <Empty title={data.screens.length ? 'No screens match these filters' : 'No screens yet'} action={!data.screens.length && can(org, 'screens.pair') ? <button className="btn btn-primary" onClick={() => setParams({ pair: '1' })}>Pair your first screen</button> : undefined}>
            {data.screens.length ? 'Clear a filter to see more.' : 'Open LumaCast Player on the device; it shows a one-time code.'}
          </Empty>
        </div>
      )}
      {view === 'grid' ? (
        <div className="grid grid-cards">
          {screens.map((s) => (
            <div key={s.id} className="card" style={{ overflow: 'hidden' }}>
              <button className="btn-ghost" style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }} onClick={() => setParams({ screen: s.id })} aria-label={`Open ${s.name}`}>
                <div className="thumb" style={{ borderRadius: 0, aspectRatio: s.orientation === 'portrait' ? '16 / 9' : '16 / 9' }}>
                  <div style={{ width: s.orientation === 'portrait' ? 54 : 120, height: s.orientation === 'portrait' ? 96 : 68, border: '3px solid var(--border-strong)', borderRadius: 6 }} aria-hidden />
                </div>
                <div style={{ padding: 16 }} className="stack">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <strong>{s.name}</strong>
                    <Status value={s.deviceId ? s.connectivity : 'unpaired'} />
                  </div>
                  <span className="subtle">{s.locationName} · {s.orientation} · {s.platform ?? 'no player'}</span>
                  <span className="subtle">
                    Heartbeat {s.heartbeatAgeSeconds === null ? 'never' : `${s.heartbeatAgeSeconds}s ago`} · release {s.activeSequence ?? '—'}/{s.desiredSequence || '—'}
                  </span>
                </div>
              </button>
              {can(org, 'screens.manage') && (
                <label className="row subtle" style={{ padding: '0 16px 12px' }}>
                  <input type="checkbox" checked={selected.has(s.id)} onChange={(e) => { const n = new Set(selected); if (e.target.checked) n.add(s.id); else n.delete(s.id); setSelected(n); }} /> Select
                </label>
              )}
            </div>
          ))}
        </div>
      ) : (
        !!screens.length && (
          <div className="card table-wrap">
            <table className="table">
              <thead><tr><th><span className="sr-only">Select</span></th><th>Screen</th><th>Location</th><th>Connection</th><th>Heartbeat</th><th>Release (active/desired)</th><th>Player</th></tr></thead>
              <tbody>
                {screens.map((s) => (
                  <tr key={s.id} aria-selected={selected.has(s.id)}>
                    <td><input type="checkbox" aria-label={`Select ${s.name}`} checked={selected.has(s.id)} onChange={(e) => { const n = new Set(selected); if (e.target.checked) n.add(s.id); else n.delete(s.id); setSelected(n); }} /></td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => setParams({ screen: s.id })}>{s.name}</button></td>
                    <td className="muted">{s.locationName}</td>
                    <td><Status value={s.deviceId ? s.connectivity : 'unpaired'} /></td>
                    <td className="muted">{s.heartbeatAgeSeconds === null ? 'never' : `${s.heartbeatAgeSeconds}s ago`}</td>
                    <td className="mono">{s.activeSequence ?? '—'} / {s.desiredSequence || '—'}</td>
                    <td className="muted">{s.platform ?? '—'} {s.appVersion ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {open && <ScreenInspector id={open} onClose={() => setParams({})} onChanged={reload} />}
      {pairing && <PairSheet onClose={() => setParams({})} onPaired={reload} />}
    </>
  );
}

function ScreenInspector({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const { data, error, reload } = useApi<{ screen: Screen; releases: { id: string; sequence: number; createdAt: string; publicationName: string; status: string }[]; deviceHistory: { id: string; platform: string; status: string; pairedAt: string; revokedReason: string | null }[] }>(orgPath(org, `/screens/${id}`), [id]);
  const cmds = useApi<{ commands: { id: string; type: string; status: string; result: Record<string, unknown> | null; issuedAt: string }[] }>(orgPath(org, `/screens/${id}/commands`), [id]);
  usePoll(useCallback(() => { void reload(); void cmds.reload(); }, [reload, cmds]), 5000);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const s = data?.screen;
  const caps = (s?.capabilities ?? {}) as Record<string, unknown>;

  async function command(type: string) {
    try {
      await api('POST', orgPath(org, `/screens/${id}/commands`), { type, ttlSeconds: 300 });
      toast(`Sent “${type.replace('_', ' ')}”. Offline players receive it when they reconnect before it expires.`);
      void cmds.reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  async function revoke() {
    try {
      const r = await api<{ note: string }>('POST', orgPath(org, `/screens/${id}/revoke-device`), { reason: 'revoked from console' });
      toast(r.note);
      setConfirmRevoke(false);
      onChanged();
      void reload();
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <Inspector title={s?.name ?? 'Screen'} onClose={onClose}>
      {error && <ErrorState error={error} />}
      {!s ? <Loading /> : (
        <div className="stack" style={{ gap: 20 }}>
          <div className="row">
            <Status value={s.deviceId ? s.connectivity : 'unpaired'} />
            {s.playerState && <Status value={s.playerState} label={`player ${s.playerState}`} />}
          </div>
          <dl className="kv">
            <dt>Location</dt><dd>{s.locationName} <span className="subtle">({s.timezone})</span></dd>
            <dt>Orientation</dt><dd>{s.orientation} · {s.width}×{s.height}</dd>
            <dt>Heartbeat</dt><dd>{s.heartbeatAgeSeconds === null ? 'never' : `${s.heartbeatAgeSeconds}s ago`}</dd>
            <dt>Desired release</dt><dd className="mono">{s.desiredSequence || '—'}</dd>
            <dt>Downloaded (staged)</dt><dd className="mono">{s.stagedSequence || '—'}</dd>
            <dt>Active (confirmed)</dt><dd className="mono">{s.activeSequence ?? '—'}</dd>
            <dt>Last confirmed play</dt><dd>{ago(s.lastConfirmedPlayAt)}</dd>
            <dt>Player</dt><dd>{s.platform ?? 'none'} {s.appVersion}</dd>
            <dt>Model</dt><dd>{String(caps.model ?? '—')}</dd>
            <dt>Decoders · max</dt><dd>{String(caps.maxVideoDecoders ?? '—')} · {String(caps.maxWidth ?? '—')}×{String(caps.maxHeight ?? '—')}</dd>
            <dt>Codecs</dt><dd>{(caps.videoCodecs as string[] | undefined)?.join(', ') ?? '—'}</dd>
            <dt>Storage</dt><dd>{formatBytes((s.lastHeartbeat?.diskFreeBytes as number) ?? null)} free of {formatBytes(caps.storageTotalBytes as number)}</dd>
            <dt>Key storage</dt><dd>{s.keyStorage ?? '—'}</dd>
            <dt>Audio</dt><dd>{s.acousticZone ? `${s.acousticZone} · ${s.isAudioMaster ? 'music master' : 'muted for music'}` : 'own music'}</dd>
          </dl>
          <p className="subtle">Preview images in LumaCast are rendered from content, not captured from the screen. This player does not support remote screenshots.</p>
          {s.deviceId && can(org, 'screens.command') && (
            <section className="stack">
              <h3>Commands</h3>
              <div className="row">
                <button className="btn btn-sm" onClick={() => void command('sync')}>Sync now</button>
                <button className="btn btn-sm" onClick={() => void command('refresh_state')}>Refresh state</button>
                <button className="btn btn-sm" onClick={() => void command('collect_diagnostics')}>Collect diagnostics</button>
                {caps.supportsAppRestart === true && <button className="btn btn-sm" onClick={() => void command('restart_app')}>Restart app</button>}
                {caps.supportsReboot !== true && <span className="subtle">Reboot and screenshot need a managed (device-owner) adapter on this model.</span>}
              </div>
              <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {cmds.data?.commands.slice(0, 6).map((c) => (
                  <li key={c.id} className="row" style={{ justifyContent: 'space-between' }}>
                    <span>{c.type.replace('_', ' ')} <span className="subtle">{ago(c.issuedAt)}</span></span>
                    <Status value={c.status} />
                    {c.result && <pre className="mono subtle" style={{ width: '100%', whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(c.result, null, 1).slice(0, 600)}</pre>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section className="stack">
            <h3>Recent releases</h3>
            {data.releases.length ? data.releases.map((r) => (
              <div key={r.id} className="row" style={{ justifyContent: 'space-between' }}>
                <span><span className="mono">#{r.sequence}</span> {r.publicationName}</span>
                <Status value={r.status} />
              </div>
            )) : <p className="subtle">Nothing published to this screen yet.</p>}
          </section>
          <section className="stack">
            <h3>Player history</h3>
            {data.deviceHistory.map((d) => (
              <div key={d.id} className="row" style={{ justifyContent: 'space-between' }}>
                <span className="mono subtle">…{d.id.slice(-4)} · {d.platform}</span>
                <Status value={d.status} label={d.revokedReason ? `${d.status} (${d.revokedReason})` : d.status} />
              </div>
            ))}
            <p className="subtle">Operator code to unpair locally on the player: last 4 characters of the active ID.</p>
          </section>
          {s.deviceId && can(org, 'screens.manage') && (
            <button className="btn btn-danger" onClick={() => setConfirmRevoke(true)}><Icon name="trash" /> Revoke player</button>
          )}
          {confirmRevoke && (
            <Sheet title="Revoke this player?" onClose={() => setConfirmRevoke(false)} footer={<><button className="btn" onClick={() => setConfirmRevoke(false)}>Cancel</button><button className="btn btn-danger solid" onClick={() => void revoke()}>Revoke</button></>}>
              <p>The player loses access to new sessions, releases and media immediately. Content already cached on a device that is physically offline cannot be erased remotely until it reconnects.</p>
            </Sheet>
          )}
        </div>
      )}
    </Inspector>
  );
}

function PairSheet({ onClose, onPaired }: { onClose: () => void; onPaired: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const locations = useApi<{ locations: { id: string; name: string }[] }>(orgPath(org, '/locations'), [org.id]);
  const screens = useApi<{ screens: Screen[] }>(orgPath(org, '/screens'), [org.id]);
  const [code, setCode] = useState('');
  const [found, setFound] = useState<{ enrollmentId: string; challenge: string; device: { platform?: string; model?: string; orientation?: string } } | null>(null);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [screenId, setScreenId] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const started = useMemo(() => Date.now(), []);

  async function lookup() {
    setErr(null);
    setBusy(true);
    try {
      const r = await api<typeof found>('POST', orgPath(org, '/pairing/lookup'), { code: code.trim().toUpperCase() });
      setFound(r);
      setName(r?.device.model ? `${r.device.model}` : '');
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function claim() {
    setBusy(true);
    setErr(null);
    try {
      await api('POST', orgPath(org, '/pairing/claim'), {
        enrollmentId: found!.enrollmentId, code: code.trim().toUpperCase(), challengeConfirmed: true,
        ...(mode === 'existing' ? { screenId } : { newScreen: { name, locationId, orientation: found!.device.orientation ?? 'landscape' } }),
      });
      toast(`Paired in ${Math.round((Date.now() - started) / 1000)}s. The player finishes setup on its own.`);
      onPaired();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title="Pair a screen" subtitle="Open LumaCast Player on the device. It shows an 8-character code." onClose={onClose}
      footer={!found ? <button className="btn btn-primary" disabled={code.replace(/[^A-Za-z0-9]/g, '').length < 8 || busy} onClick={() => void lookup()}>Continue</button>
        : <button className="btn btn-primary" disabled={!confirmed || busy || (mode === 'new' ? !name || !locationId : !screenId)} onClick={() => void claim()}>Pair screen</button>}>
      <div className="stack" style={{ gap: 16 }}>
        {err && <div className="alert danger" role="alert">{err}</div>}
        {!found ? (
          <div className="field">
            <label htmlFor="pair-code">Pairing code</label>
            <input id="pair-code" className="input code-display" maxLength={9} placeholder="ABCD-2345" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && void lookup()} />
            <span className="hint">Codes work once and expire after 10 minutes.</span>
          </div>
        ) : (
          <>
            <div className="card card-pad stack" style={{ background: 'var(--surface-2)' }}>
              <span className="subtle">The player should show this confirmation number:</span>
              <div className="code-display" aria-label={`Confirmation ${found.challenge}`}>{found.challenge}</div>
              <span className="subtle">{found.device.model} · {found.device.platform} · {found.device.orientation}</span>
              <label className="row"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} /> The number on the screen matches</label>
            </div>
            <div className="segmented" role="group" aria-label="Screen">
              <button aria-pressed={mode === 'new'} onClick={() => setMode('new')}>New screen</button>
              <button aria-pressed={mode === 'existing'} onClick={() => setMode('existing')}>Replace existing player</button>
            </div>
            {mode === 'new' ? (
              <>
                <div className="field"><label htmlFor="pair-name">Screen name</label><input id="pair-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Front window TV" /></div>
                <div className="field">
                  <label htmlFor="pair-loc">Location</label>
                  <select id="pair-loc" className="input" value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                    <option value="">Choose a location</option>
                    {locations.data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div className="field">
                <label htmlFor="pair-screen">Screen</label>
                <select id="pair-screen" className="input" value={screenId} onChange={(e) => setScreenId(e.target.value)}>
                  <option value="">Choose a screen</option>
                  {screens.data?.screens.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.locationName}{s.deviceId ? ' (replaces current player)' : ''}</option>)}
                </select>
                <span className="hint">Replacing keeps the screen’s location, schedule and history and revokes the old player.</span>
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}
